import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ActionsMenu } from '~src/spacesPlatform/components/ActionsMenu/ActionsMenu';
import { IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { ConnectedSpaceHeader } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpaceInfoItemType } from '~src/spacesPlatform/models/ContentType';

import { ISpaceItemDetailInfo } from '../../../../spacesPlatform/models/ISpaceItemDetailInfo';
import { IGroupInfo } from '../../models/IGroupInfo';

export interface GroupSpaceHeaderProps {
  readonly item: ISpaceItemDetailInfo<'group'>;
}

export function GroupSpaceHeader(props: GroupSpaceHeaderProps): JSX.Element {
  return <ConnectedSpaceHeader
      item={props.item}
      detailIcons={
        <React.Fragment>
          {GroupActionsMenuIcon(props.item)}
        </React.Fragment>
      }
      metadataElement={
        <React.Fragment>
          <MetaDataRow {...props} />
        </React.Fragment>
      }
  />;
}

export function MetaDataRow(props: GroupSpaceHeaderProps) {
  return (
    <>
      <td tb-test-id="header-created-label">{Messages.column_created()}</td>
      <td className={SpaceHeaderStyles.GroupDateValue} tb-test-id="header-created-id">{L10n.localizedDateTime(props.item.createdAt)}</td>
      <td tb-test-id="header-modified-label">{Messages.column_lastModified()}</td>
      <td className={SpaceHeaderStyles.GroupDateValue} tb-test-id="header-modified-id">{L10n.localizedDateTime(props.item.updatedAt)}</td>
    </>
  );
}

interface NameRowButtonOwnProps {
  readonly iconPath: string;
  readonly iconActivePath: string;
  readonly action: IEntityAction<IGroupInfo, {}>;
  readonly item: ISpaceItemDetailInfo<'group'>;
}

interface NameRowButtonStateProps {
  readonly isVisible: boolean;
}

interface NameRowButtonDispatchProps {
  readonly onClick: () => void;
}

type NameRowButtonProps = NameRowButtonOwnProps & NameRowButtonStateProps & NameRowButtonDispatchProps;

function NameRowButton(props: NameRowButtonProps) {
  if (!props.isVisible) {
    return null;
  }

  return (
    <ButtonWidget
      buttonType={ButtonType.LowEmphasis}
      getContent={getDetailButtonContent(props.iconPath, props.iconActivePath)}
      title={props.action.translateName()}
      testId={props.action.name}
      handleClick={props.onClick}
      style={SpaceHeaderStyles.IconButtonStyleOverrides}
    />
  );
}

const getDetailButtonContent = (iconPath: string, activePath: string): (state: InteractionState) => JSX.Element => {
  return (state: InteractionState): JSX.Element => {
    const showActiveIcon = state.hover || state.active || state.focus;
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <img
        className={SpaceHeaderStyles.HeaderButtonImage}
        src={showActiveIcon ? activePath : iconPath }
      />
    );
  };
};

const mapStateToProps: MapStateToProps<NameRowButtonStateProps> = (state: AppState, ownProps: NameRowButtonOwnProps) => ({
  isVisible: ownProps.action.isClickable({}, [ownProps.item], state) && ownProps.action.isVisible([ownProps.item], state)
});

const mapDispatchToProps: MapDispatchToProps<NameRowButtonDispatchProps> = (dispatch, ownProps: NameRowButtonOwnProps) => {
  return {
    onClick: () => {
      dispatch(ownProps.action.createReduxAction([ownProps.item]));
    }
  };
};

export const ConnectedNameRowButton: React.ComponentClass<NameRowButtonOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(NameRowButton);

export function GroupActionsMenuIcon<C extends SpaceInfoItemType>(
  item: ISpaceItemDetailInfo<C>,
  disabled?: boolean,
  buttonTitle?: string,
): JSX.Element {
  return <ActionsMenu buttonTitle={buttonTitle} targetItems={[ item ]} useIcon={true} disabled={disabled}/>;
}
